<template>
  <div class="cheque-payment-voucher mb-3 ml-1" ref="rootEl">
    <div class="cpv-header">
      <p v-if="companyName" class="cpv-company-name">{{ companyName }}</p>
      <h3 class="cpv-title">CHEQUE PAYMENT VOUCHER</h3>
    </div>

    <div class="cpv-field cpv-field-no">
      <div class="cpv-field-no-main">
        <span class="cpv-label">No.</span>
        <span class="cpv-readonly cpv-readonly-short">{{ voucherNo || '-' }}</span>
      </div>
      <span v-if="bankAmountDifference > 0" class="cpv-bank-diff">{{ fmt(bankAmountDifference) }}</span>
    </div>

    <div class="cpv-field">
      <span class="cpv-label">Paid to</span>
      <span class="cpv-readonly cpv-readonly-full">{{ paidTo || '-' }}</span>
    </div>

    <div class="cpv-field cpv-amount-row">
      <div class="cpv-amount-words">
        <span class="cpv-label">Amount in words</span>
        <input
          v-model="local.amountInWords"
          type="text"
          class="cpv-line cpv-line-full"
          autocomplete="off"
        >
      </div>
      <div class="cpv-amount-figure">
        <span class="cpv-label">Amount in figures</span>
        <span class="cpv-figure-box cpv-figure-readonly">{{ amountInFiguresDisplay }}</span>
      </div>
    </div>

    <div class="cpv-field">
      <span class="cpv-label">Purpose of payment</span>
      <input
        v-model="local.purposeOfPayment"
        type="text"
        class="cpv-line cpv-line-full"
        autocomplete="off"
      >
    </div>

    <div class="cpv-field cpv-bank-row">
      <div class="cpv-bank cpv-bank-picker">
        <span class="cpv-label">
          Bank
          <span class="cpv-required" aria-hidden="true">*</span>
        </span>
        <div class="cpv-bank-input-wrap">
          <button
       
            type="button"
            class="cpv-bank-display-btn"
            :class="{ 'cpv-bank-display-btn--error': bankHighlightError }"
            @click.stop="toggleBankOpen"
          >
            {{ bankButtonLabel }} ▾
          </button>
          <div
            v-if="bankOpen && activeBanks.length > 0"
            class="cpv-bank-dropdown cpv-bank-dropdown--amounts"
            @click.stop
          >
            <div class="cpv-bank-dropdown-title">Bank · Amount · Cheque no</div>
            <div
              v-for="bank in activeBanks"
              :key="bank.id"
              class="cpv-bank-amount-row"
            >
              <label class="cpv-bank-amount-label">{{ bank.bankName }}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                :value="bankPaymentAmount(bank.id)"
                class="cpv-bank-amount-input"
                placeholder="0.00"
                @input="setBankPayment(bank.id, $event.target.value)"
              >
              <input
                type="text"
                :value="bankChequeNo(bank.id)"
                class="cpv-bank-cheque-input"
                placeholder="Cheque no"
                autocomplete="off"
                @input="setBankChequeNo(bank.id, $event.target.value)"
              >
            </div>
          </div>
          <p v-else-if="bankOpen && activeBanks.length === 0" class="cpv-bank-empty">
            No active banks saved.
          </p>
        </div>
      </div>
    </div>

    <table class="cpv-ledger-table" cellspacing="0" cellpadding="0">
      <thead>
        <tr>
          <th class="cpv-col-code">Code</th>
          <th class="cpv-col-desc">Description</th>
          <th colspan="2" class="cpv-col-debit">Debit</th>
          <th colspan="2" class="cpv-col-credit">Credit</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="cpv-cell-readonly"></td>
          <td class="cpv-cell-readonly"></td>
          <td class="cpv-split cpv-cell-readonly"></td>
          <td class="cpv-split cpv-cell-readonly"></td>
          <td class="cpv-split cpv-cell-readonly"></td>
          <td class="cpv-split cpv-cell-readonly"></td>
        </tr>
      </tbody>
    </table>

    <div class="cpv-signatures cpv-signatures-row pt-5 pb-3">
      <div class="cpv-signature">
        <span class="cpv-sig-label">Prepared by</span>
        <span class="cpv-sig-readonly cpv-sig-line">{{ preparedByDisplay }}</span>
        <span class="cpv-sig-slash">/</span>
        <input v-model="local.preparedDate" type="text" class="cpv-sig-line cpv-sig-line-date" autocomplete="off">
      </div>
      <div class="cpv-signature">
        <span class="cpv-sig-label">Checked by</span>
        <span class="cpv-sig-readonly cpv-sig-line"></span>
        <span class="cpv-sig-slash">/</span>
        <input
          type="text"
          class="cpv-sig-line cpv-sig-line-date cpv-sig-readonly-input"
          readonly
          tabindex="-1"
          autocomplete="off"
        >
      </div>
      <div class="cpv-signature">
        <span class="cpv-sig-label">Approved by</span>
        <span class="cpv-sig-readonly cpv-sig-line"></span>
        <span class="cpv-sig-slash">/</span>
        <input
          type="text"
          class="cpv-sig-line cpv-sig-line-date cpv-sig-readonly-input"
          readonly
          tabindex="-1"
          autocomplete="off"
        >
      </div>
      <div class="cpv-signature">
        <span class="cpv-sig-label">Received by</span>
        <span class="cpv-sig-readonly cpv-sig-line"></span>
        <span class="cpv-sig-slash">/</span>
        <input
          type="text"
          class="cpv-sig-line cpv-sig-line-date cpv-sig-readonly-input"
          readonly
          tabindex="-1"
          autocomplete="off"
        >
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineExpose, defineProps, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'

const props = defineProps({
  companyName: { type: String, default: '' },
  voucherNo: { type: String, default: '' },
  paidTo: { type: String, default: '' },
  amountInFigures: { type: Number, default: 0 },
  activeBanks: { type: Array, default: () => [] },
  bankError: { type: Boolean, default: false },
  /** Display name for Prepared by (logged user or saved preparer). */
  preparedByName: { type: String, default: '' }
})

const emit = defineEmits(['bank-selected', 'bank-limit-exceeded'])

const rootEl = ref(null)
const bankOpen = ref(false)
const bankPayments = ref({})
const bankChequeNos = ref({})
const bankPaymentsChecked = ref({}) // { [bankId]: 0|1 } preserved from saved bank_payments

const local = reactive({
  amountInWords: '',
  purposeOfPayment: '',
  preparedDate: ''
})

function n(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function fmt(v) {
  return n(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const preparedByDisplay = computed(() => {
  const name = String(props.preparedByName || '').trim()
  return name || '—'
})

const grandBankPayments = computed(() => {
  let sum = 0
  for (const v of Object.values(bankPayments.value)) {
    sum += n(v)
  }
  return Math.round(sum * 100) / 100
})

const bankButtonLabel = computed(() => {
  const count = Object.values(bankPayments.value).filter((v) => n(v) > 0).length
  if (count > 0) return `Edit (${fmt(grandBankPayments.value)})`
  return 'Select bank'
})

const bankHighlightError = computed(() => props.bankError && !hasValidBank())

const bankAmountDifference = computed(() => {
  const max = n(props.amountInFigures)
  const paid = grandBankPayments.value
  const diff = Math.round((max - paid) * 100) / 100
  return diff > 0.001 ? diff : 0
})

const amountInFiguresDisplay = computed(() => {
  const val = Number(props.amountInFigures)
  return Number.isFinite(val) ? val.toFixed(2) : '0.00'
})

function bankPaymentAmount(bankId) {
  const v = bankPayments.value[String(bankId)]
  return v !== undefined && n(v) !== 0 ? String(v) : ''
}

function bankChequeNo(bankId) {
  return bankChequeNos.value[String(bankId)] || ''
}

function setBankChequeNo(bankId, raw) {
  const id = String(bankId)
  const val = String(raw ?? '').trim()
  if (!val) {
    const next = { ...bankChequeNos.value }
    delete next[id]
    bankChequeNos.value = next
    return
  }
  bankChequeNos.value = { ...bankChequeNos.value, [id]: val }
}

function setBankPayment(bankId, raw) {
  const id = String(bankId)
  const val = n(raw)
  if (val <= 0 || raw === '' || raw == null) {
    const next = { ...bankPayments.value }
    delete next[id]
    bankPayments.value = next
    const nextCheque = { ...bankChequeNos.value }
    delete nextCheque[id]
    bankChequeNos.value = nextCheque
    emit('bank-selected')
    return
  }
  const otherSum = Object.entries(bankPayments.value)
    .filter(([k]) => k !== id)
    .reduce((a, [, v]) => a + n(v), 0)
  const newTotal = Math.round((otherSum + val) * 100) / 100
  const maxAmount = n(props.amountInFigures)
  if (newTotal > maxAmount + 0.001) {
    emit(
      'bank-limit-exceeded',
      `Total bank amounts (${fmt(newTotal)}) cannot exceed Amount in figures (${fmt(maxAmount)}).`
    )
    return
  }
  bankPayments.value = { ...bankPayments.value, [id]: val }
  if (!(id in bankPaymentsChecked.value)) {
    bankPaymentsChecked.value = { ...bankPaymentsChecked.value, [id]: 0 }
  }
  emit('bank-selected')
}

function buildBankPaymentsPayload() {
  return (props.activeBanks || [])
    .map((bank) => ({
      bank_id: Number(bank.id),
      bank_name: String(bank.bankName || '').trim(),
      amount: n(bankPayments.value[String(bank.id)]),
      cheque_no: String(bankChequeNos.value[String(bank.id)] || '').trim(),
      is_checked: bankPaymentsChecked.value[String(bank.id)] ?? 0,
    }))
    .filter((b) => Number.isFinite(b.bank_id) && b.bank_id > 0 && b.amount > 0)
}

function hasValidBank() {
  return !getBankValidationMessage()
}

function getBankValidationMessage() {
  const list = buildBankPaymentsPayload()
  if (!list.length) {
    return 'Please enter at least one bank amount on the cheque payment voucher.'
  }
  for (const line of list) {
    if (line.amount > 0 && !String(line.cheque_no || '').trim()) {
      const label = String(line.bank_name || '').trim() || 'bank'
      return `Cheque number is required for ${label}.`
    }
  }
  const maxAmount = n(props.amountInFigures)
  const sum = list.reduce((acc, b) => acc + n(b.amount), 0)
  if (sum <= 0 || sum > maxAmount + 0.001) {
    return 'Please enter valid bank amounts on the cheque payment voucher.'
  }
  return ''
}

function toggleBankOpen() {
  bankOpen.value = !bankOpen.value
}

function onDocumentClick(evt) {
  if (!bankOpen.value) return
  const root = rootEl.value
  if (root && !root.contains(evt.target)) {
    bankOpen.value = false
  }
}

const skipBankResetOnVoucherChange = ref(false)

watch(
  () => props.voucherNo,
  () => {
    if (!skipBankResetOnVoucherChange.value) {
      bankPayments.value = {}
      bankChequeNos.value = {}
      bankPaymentsChecked.value = {}
    }
  }
)

function parseBankPaymentsFromState(state = {}) {
  const amountMap = {}
  const chequeMap = {}
  const checkedMap = {}
  if (Array.isArray(state.bankPayments)) {
    for (const line of state.bankPayments) {
      const id = String(line?.bank_id ?? line?.bankId ?? '')
      const amt = Number(line?.amount || 0)
      const cheque = String(line?.cheque_no ?? line?.chequeNo ?? '').trim()
      if (id && Number.isFinite(amt) && amt > 0) amountMap[id] = amt
      if (id && cheque) chequeMap[id] = cheque
      if (id) {
        const checked = line?.is_checked ?? line?.isChecked
        checkedMap[id] = Number(checked) === 1 || checked === true ? 1 : 0
      }
    }
  }
  if (!Object.keys(amountMap).length && state.bankId != null && state.bankId !== '') {
    const legacyAmount = n(props.amountInFigures)
    if (legacyAmount > 0) {
      amountMap[String(state.bankId)] = legacyAmount
    }
  }
  if (state.chequeNo != null && String(state.chequeNo).trim() !== '') {
    const legacyCheque = String(state.chequeNo).trim()
    const targetId = Object.keys(amountMap)[0] || String(state.bankId ?? '')
    if (targetId && !chequeMap[targetId]) {
      chequeMap[targetId] = legacyCheque
    }
  }
  return { amountMap, chequeMap, checkedMap }
}

function getFormState() {
  const list = buildBankPaymentsPayload()
  return {
    amountInWords: local.amountInWords,
    purposeOfPayment: local.purposeOfPayment,
    chequeNo: list[0]?.cheque_no || '',
    bankId: list[0]?.bank_id ?? null,
    bankPayments: list,
    bankAmountDifference: bankAmountDifference.value,
    preparedDate: local.preparedDate
  }
}

function setFormState(state = {}) {
  skipBankResetOnVoucherChange.value = true
  local.amountInWords = state.amountInWords != null ? String(state.amountInWords) : ''
  local.purposeOfPayment = state.purposeOfPayment != null ? String(state.purposeOfPayment) : ''
  local.preparedDate = state.preparedDate != null ? String(state.preparedDate) : ''
  const parsed = parseBankPaymentsFromState(state)
  bankPayments.value = parsed.amountMap
  bankChequeNos.value = parsed.chequeMap
  bankPaymentsChecked.value = parsed.checkedMap
  window.setTimeout(() => {
    skipBankResetOnVoucherChange.value = false
  }, 0)
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
})

defineExpose({
  getFormState,
  setFormState,
  hasValidBank,
  getBankValidationMessage
})
</script>

<style scoped>
.cheque-payment-voucher {
  --cpv-form-width: 30rem;
  font-family: 'Times New Roman', Times, serif;
  font-size: 11px;
  color: #000;
  line-height: 1.25;
  width: var(--cpv-form-width);
  min-width: var(--cpv-form-width);
  max-width: var(--cpv-form-width);
  margin-top: 0.35rem;
  padding: 0.5rem 0.65rem 0.6rem;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  box-sizing: border-box;
}

.cpv-header {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.35rem;
}

.cpv-company-name {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-align: center;
  color: #0d4169;
  line-height: 1.25;
  word-break: break-word;
}

.cpv-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: underline;
  text-transform: uppercase;
  text-align: left;
  line-height: 1.25;
}

.cpv-field {
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
  margin-bottom: 0.3rem;
}

.cpv-label {
  flex-shrink: 0;
  font-size: 11px;
  white-space: nowrap;
}

.cpv-readonly {
  display: inline-block;
  border-bottom: 1px solid #000;
  padding: 0 0 1px;
  font-size: 11px;
  line-height: 1.2;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cpv-readonly-short {
  flex: 0 1 5rem;
  max-width: 5rem;
  min-width: 4rem;
}

.cpv-readonly-full {
  flex: 1;
  min-width: 0;
}

.cpv-line {
  flex: 1;
  min-width: 0;
  border: none;
  border-bottom: 1px solid #000;
  border-radius: 0;
  background: transparent;
  padding: 0 0 1px;
  font-family: inherit;
  font-size: 11px;
  line-height: 1.2;
  outline: none;
}

.cpv-line-medium {
  flex: 1;
  min-width: 4rem;
}

.cpv-line-full {
  flex: 1;
}

.cpv-field-no {
  width: 100%;
  max-width: var(--cpv-form-width);
  justify-content: space-between;
  align-items: flex-end;
}

.cpv-field-no-main {
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
  min-width: 0;
}

.cpv-bank-diff {
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 0.35rem;
  font-size: 11px;
  font-weight: 700;
  color: #dc2626;
  line-height: 1.2;
}

.cpv-amount-row {
  align-items: flex-end;
  gap: 0.5rem;
}

.cpv-amount-words {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
  min-width: 0;
}

.cpv-amount-figure {
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 0.35rem;
}

.cpv-figure-box {
  display: inline-flex;
  align-items: center;
  width: 6.5rem;
  height: 1.35rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.25rem;
  background: #f8fafc;
  padding: 0 0.35rem;
  font-family: inherit;
  font-size: 11px;
  box-sizing: border-box;
}

.cpv-figure-readonly {
  justify-content: flex-end;
  font-weight: 600;
  color: #1e293b;
  cursor: default;
}

.cpv-bank-row {
  display: flex;
  gap: 0.75rem;
}

.cpv-bank {
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;
}

.cpv-bank-picker {
  position: relative;
}

.cpv-bank-input-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
}

.cpv-required {
  color: #dc2626;
  font-weight: 700;
}

.cpv-bank-display-btn {
  width: 100%;
  min-height: 1.35rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.25rem;
  background: #f8fafc;
  padding: 0.15rem 0.45rem;
  font-family: inherit;
  font-size: 11px;
  text-align: left;
  color: #1e293b;
  cursor: pointer;
}

.cpv-bank-display-btn:hover {
  border-color: #0d4169;
}

.cpv-bank-display-btn--error {
  border-color: #dc2626;
  background: #fef2f2;
}

.cpv-bank-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 20;
  margin: 0.1rem 0 0;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 0.25rem;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
  text-align: left;
}

.cpv-bank-dropdown--amounts {
  width: 25rem;      /* reduce from current size */
  min-width: 25rem;
  max-width: 25rem;
  max-height: 12rem;
  overflow-y: auto;
}

.cpv-bank-dropdown-title {
  padding: 0.35rem 0.55rem;
  font-size: 10px;
  font-weight: 700;
  color: #0d4169;
  border-bottom: 1px solid #e2e8f0;
}

.cpv-bank-amount-row {
  display: grid;
  grid-template-columns: 1fr 5.5rem 5.5rem;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.55rem;
  border-bottom: 1px solid #f1f5f9;
}

.cpv-bank-amount-row:hover {
  background: #f8fafc;
}

.cpv-bank-amount-label {
  flex: 1;
  min-width: 0;
  font-size: 10px;
  font-weight: 600;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cpv-bank-amount-input {
  width: 5.5rem;
  flex-shrink: 0;
  border: 1px solid #cbd5e1;
  border-radius: 0.2rem;
  padding: 0.15rem 0.25rem;
  font-family: inherit;
  font-size: 10px;
  text-align: center;
  font-weight: 600;
  color: #0d4169;
  outline: none;
}

.cpv-bank-amount-input:focus {
  border-color: #0d4169;
}

.cpv-bank-cheque-input {
  width: 5.5rem;
  flex-shrink: 0;
  border: 1px solid #cbd5e1;
  border-radius: 0.2rem;
  padding: 0.15rem 0.25rem;
  font-family: inherit;
  font-size: 10px;
  text-align: center;
  outline: none;
}

.cpv-bank-cheque-input:focus {
  border-color: #0d4169;
}

.cpv-bank-empty {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 20;
  margin: 0.1rem 0 0;
  padding: 0.35rem 0.45rem;
  font-size: 10px;
  color: #64748b;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 0.25rem;
  text-align: left;
}

.cpv-ledger-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0.35rem 0 0.45rem;
  table-layout: fixed;
}

.cpv-ledger-table th,
.cpv-ledger-table td {
  border: 1px solid #000;
  padding: 0;
  vertical-align: middle;
  text-align: center;
  font-weight: 400;
}

.cpv-ledger-table th {
  font-size: 11px;
  padding: 0.15rem 0.1rem;
}

.cpv-col-code {
  width: 12%;
}

.cpv-col-desc {
  width: 40%;
}

.cpv-col-debit,
.cpv-col-credit {
  width: 24%;
}

.cpv-ledger-table tbody td.cpv-cell-readonly {
  height: 1.35rem;
  background: #fafafa;
}

.cpv-split {
  width: 12%;
}

.cpv-signatures-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 0.25rem;
  margin-top: 0.15rem;
}

.cpv-signature {
  flex: 1 1 0;
  display: flex;
  align-items: flex-end;
  gap: 0.12rem;
  min-width: 0;
}

.cpv-sig-label {
  flex-shrink: 0;
  font-size: 9px;
  white-space: nowrap;
}

.cpv-sig-line {
  flex: 1 1 0;
  min-width: 0.85rem;
  border: none;
  border-bottom: 1px solid #000;
  background: transparent;
  padding: 0 0 1px;
  font-family: inherit;
  font-size: 9px;
  outline: none;
}

.cpv-sig-readonly {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1e293b;
}

.cpv-sig-readonly-input {
  cursor: default;
  background: transparent;
}

.cpv-sig-line-date {
  flex: 0 1 2.1rem;
  max-width: 2.35rem;
}

.cpv-sig-slash {
  flex-shrink: 0;
  padding: 0 0.05rem 1px;
}

@media (max-width: 639px) {
  .cheque-payment-voucher {
    --cpv-form-width: 100%;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    font-size: 12px;
    padding: 0.65rem 0.5rem;
  }

  .cpv-field {
    flex-wrap: wrap;
  }

  .cpv-field-no {
    max-width: none;
  }

  .cpv-amount-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.4rem;
  }

  .cpv-amount-words,
  .cpv-amount-figure {
    width: 100%;
  }

  .cpv-amount-figure {
    justify-content: space-between;
  }

  .cpv-figure-box {
    flex: 1;
    max-width: 8rem;
  }

  .cpv-bank-row {
    flex-direction: column;
    gap: 0.4rem;
  }

  .cpv-bank {
    width: 100%;
  }

  .cpv-line,
  .cpv-line-medium,
  .cpv-line-full {
    min-height: 2rem;
    font-size: 1rem;
  }

  .cpv-bank-display-btn {
    min-height: 2rem;
    font-size: 1rem;
  }

  .cpv-signatures-row {
    flex-wrap: wrap;
    gap: 0.5rem 0.35rem;
  }

  .cpv-signature {
    flex: 1 1 calc(50% - 0.25rem);
    min-width: 7.5rem;
  }

  .cpv-sig-label {
    font-size: 8px;
  }

  .cpv-sig-line {
    font-size: 0.75rem;
    min-height: 1.5rem;
  }

  .cpv-ledger-table th {
    font-size: 10px;
  }

  .cpv-ledger-table tbody td.cpv-cell-readonly {
    height: 1.75rem;
  }
}
</style>
